import * as React from "react"
import { createPortal } from "react-dom"
import { Listbox, Transition } from "@headlessui/react"
import { Check, ChevronDown } from "lucide-react"
import { cn } from "../../utils"

// Context to manage select state and children mapping
interface SelectContextValue {
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  selectedLabel?: string
  setSelectedLabel?: (label: string) => void
  triggerRef?: React.RefObject<HTMLButtonElement>
  isOpen?: boolean
}

const SelectContext = React.createContext<SelectContextValue>({})
const SelectOpenContext = React.createContext<boolean>(false)

// Helper function to extract text content from React children
const extractTextFromChildren = (children: React.ReactNode): string => {
  if (typeof children === "string" || typeof children === "number") {
    return String(children)
  }

  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join("")
  }

  if (React.isValidElement(children)) {
    const props = children.props as { children?: React.ReactNode }
    if (props.children) {
      return extractTextFromChildren(props.children)
    }
  }

  return ""
}

// Root component
interface SelectProps {
  value?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
}

const Select: React.FC<SelectProps> = ({ value, onValueChange, children }) => {
  const findLabelFromChildren = React.useCallback(
    (children: React.ReactNode, targetValue: string): string | undefined => {
      let foundLabel: string | undefined

      React.Children.forEach(children, (child) => {
        if (foundLabel) return

        if (React.isValidElement(child)) {
          const childProps = child.props as any
          const childType = child.type as any

          if (
            childType?.displayName === "SelectContent" ||
            childType?.name === "SelectContent"
          ) {
            foundLabel = findLabelFromChildren(childProps.children, targetValue)
          } else if (
            (childType?.displayName === "SelectItem" ||
              childType?.name === "SelectItem") &&
            childProps.value === targetValue
          ) {
            foundLabel =
              extractTextFromChildren(childProps.children) || targetValue
          } else if (childProps?.children) {
            foundLabel = findLabelFromChildren(
              childProps.children,
              targetValue
            )
          }
        }
      })

      return foundLabel
    },
    []
  )

  const [selectedLabel, setSelectedLabel] = React.useState<string>(() => {
    if (!value) return ""
    return findLabelFromChildren(children, value) || ""
  })

  const triggerRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (!value) {
      setSelectedLabel("")
      return
    }

    const label = findLabelFromChildren(children, value)
    if (label) {
      setSelectedLabel(label)
    }
  }, [value, children, findLabelFromChildren])

  return (
    <SelectContext.Provider
      value={{ value, onValueChange, selectedLabel, setSelectedLabel, triggerRef }}
    >
      <Listbox value={value} onChange={onValueChange}>
        {({ open }) => (
          <SelectOpenContext.Provider value={open}>
            <div className="relative">{children}</div>
          </SelectOpenContext.Provider>
        )}
      </Listbox>
    </SelectContext.Provider>
  )
}

const SelectGroup = ({ children }: { children: React.ReactNode }) => (
  <>{children}</>
)

// Value placeholder component
const SelectValue = ({ placeholder }: { placeholder?: string }) => {
  const { selectedLabel } = React.useContext(SelectContext)
  return (
    <span className={cn("truncate min-w-0", !selectedLabel ? "text-muted-foreground" : "")}>
      {selectedLabel || placeholder}
    </span>
  )
}

// Trigger component
const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof Listbox.Button>
>(({ className, children, ...props }, forwardedRef) => {
  const { triggerRef } = React.useContext(SelectContext)

  // Compose refs - forward ref and internal trigger ref
  const composedRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      // Forward ref
      if (typeof forwardedRef === "function") {
        forwardedRef(node)
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLButtonElement | null>).current = node
      }
      // Internal ref for positioning
      if (triggerRef) {
        (triggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node
      }
    },
    [forwardedRef, triggerRef]
  )

  return (
    <Listbox.Button
      ref={composedRef}
      className={cn(
        "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:border-indigo-500",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      {typeof children === "function" ? children({} as any) : children}
      <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
    </Listbox.Button>
  )
})

SelectTrigger.displayName = "SelectTrigger"

// Content component - portaled to body for proper z-index and no clipping
const SelectContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({ className, children, ...props }, forwardedRef) => {
  const { triggerRef } = React.useContext(SelectContext)
  const isOpen = React.useContext(SelectOpenContext)
  const internalRef = React.useRef<HTMLElement | null>(null)
  const VIEWPORT_EDGE_PADDING = 8

  // Compose forwarded ref with internal ref
  const composedRef = React.useCallback(
    (node: HTMLElement | null) => {
      internalRef.current = node
      // Use setProperty with !important so no external CSS rule (e.g. from
      // micro-frontend stylesheets loaded via module federation) can override
      // the z-index and push the dropdown behind a Dialog overlay.
      if (node) {
        node.style.setProperty("z-index", "9999999", "important")
      }
      if (typeof forwardedRef === "function") forwardedRef(node as any)
      else if (forwardedRef) (forwardedRef as React.MutableRefObject<any>).current = node
    },
    [forwardedRef]
  )

  // Fix: Allow scrolling inside the dropdown even when a Radix Dialog scroll lock is active.
  // react-remove-scroll (used by Radix Dialog) adds a capture-phase wheel listener on `document`
  // that calls preventDefault() for elements portaled outside the dialog content.
  // By adding our own capture-phase listener on `window` (which fires before `document` in the
  // capture phase), we stop propagation for wheel/touch events targeting the dropdown, so the
  // scroll lock never sees them and native CSS overflow scrolling proceeds normally.
  React.useEffect(() => {
    if (!isOpen) return

    const handleWheel = (e: WheelEvent) => {
      if (internalRef.current?.contains(e.target as Node)) {
        e.stopPropagation()
      }
    }
    const handleTouchMove = (e: TouchEvent) => {
      if (internalRef.current?.contains(e.target as Node)) {
        e.stopPropagation()
      }
    }

    window.addEventListener("wheel", handleWheel, { capture: true })
    window.addEventListener("touchmove", handleTouchMove, { capture: true })

    return () => {
      window.removeEventListener("wheel", handleWheel, { capture: true })
      window.removeEventListener("touchmove", handleTouchMove, { capture: true })
    }
  }, [isOpen])

  const [position, setPosition] = React.useState<{
    top?: number
    bottom?: number
    left: number
    width: number
    openUpward: boolean
  } | null>(null)

  const clampLeftToViewport = React.useCallback((preferredLeft: number, menuWidth: number) => {
    const maxLeft = Math.max(
      VIEWPORT_EDGE_PADDING,
      window.innerWidth - menuWidth - VIEWPORT_EDGE_PADDING
    )
    return Math.min(Math.max(preferredLeft, VIEWPORT_EDGE_PADDING), maxLeft)
  }, [])

  // Calculate position when dropdown opens - use fixed positioning for portal
  // Automatically flip upward if not enough space below
  React.useLayoutEffect(() => {
    if (!isOpen || !triggerRef?.current) {
      setPosition(null)
      return
    }

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (rect) {
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - rect.bottom
        const spaceAbove = rect.top
        const dropdownHeight = 240 // max-h-60 = 15rem = 240px
        const measuredMenuWidth =
          internalRef.current?.getBoundingClientRect().width ?? rect.width
        const safeLeft = clampLeftToViewport(rect.left, measuredMenuWidth)

        // Open upward if not enough space below but enough above
        const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

        if (openUpward) {
          setPosition({
            bottom: viewportHeight - rect.top + 4, // 4px gap above trigger
            left: safeLeft,
            width: rect.width,
            openUpward: true,
          })
        } else {
          setPosition({
            top: rect.bottom + 4, // 4px gap below trigger
            left: safeLeft,
            width: rect.width,
            openUpward: false,
          })
        }
      }
    }

    // Calculate immediately
    updatePosition()

    // Recalculate after dropdown renders so we can clamp based on actual menu width.
    const rafId = window.requestAnimationFrame(updatePosition)

    // Update position on scroll/resize to keep dropdown aligned with trigger
    window.addEventListener("scroll", updatePosition, true)
    window.addEventListener("resize", updatePosition)

    return () => {
      window.cancelAnimationFrame(rafId)
      window.removeEventListener("scroll", updatePosition, true)
      window.removeEventListener("resize", updatePosition)
    }
  }, [isOpen, triggerRef, clampLeftToViewport])

  // Don't render anything if not open
  if (!isOpen) {
    return null
  }

  const dropdownContent = (
    <Listbox.Options
      ref={composedRef}
      static // Keep it mounted when we control visibility
      style={{
        position: "fixed",
        top: position?.openUpward ? undefined : (position?.top ?? 0),
        bottom: position?.openUpward ? position?.bottom : undefined,
        left: position?.left ?? 0,
        minWidth: position?.width ?? 100,
        maxWidth: `calc(100vw - ${VIEWPORT_EDGE_PADDING * 2}px)`,
        visibility: position ? "visible" : "hidden",
        zIndex: 9999999,
      }}
      className={cn(
        // Must be ABOVE PopoverContent z-index (z-[999999]) so selects inside popovers aren't hidden
        "z-[9999999] max-h-60 min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg",
        "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent",
        "[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb:hover]:bg-gray-400",
        className
      )}
      {...props}
    >
      {children}
    </Listbox.Options>
  )

  // Always portal to document.body to escape all stacking contexts
  if (typeof document !== "undefined") {
    return createPortal(dropdownContent, document.body)
  }

  return dropdownContent
})

SelectContent.displayName = "SelectContent"

// Item component
const SelectItem = React.forwardRef<
  HTMLDivElement,
  { className?: string; children: React.ReactNode; value: string }
>(({ className, children, value, ...props }, _ref) => {
  const { value: selectedValue, setSelectedLabel } =
    React.useContext(SelectContext)

  React.useEffect(() => {
    if (selectedValue === value && setSelectedLabel) {
      const label = extractTextFromChildren(children) || value
      setSelectedLabel(label)
    }
  }, [selectedValue, value, children, setSelectedLabel])

  const shouldHideTick = className?.includes(
    "[&>span:first-child]:hidden"
  )

  return (
    <Listbox.Option
      value={value}
      className={({ active, selected }) =>
        cn(
          "relative flex w-full cursor-pointer select-none items-center justify-between rounded-sm py-1.5 px-2 text-sm outline-none",
          active ? "bg-accent text-accent-foreground" : "",
          shouldHideTick && selected
            ? "bg-blue-600 text-white rounded-md"
            : "",
          className
        )
      }
      {...props}
    >
      {({ selected }) => (
        <>
          <span className="flex-1 text-left">{children}</span>
          <span
            className={cn(
              "ml-2 flex h-4 w-4 items-center justify-center shrink-0",
              selected && !shouldHideTick ? "opacity-100" : "opacity-0",
              shouldHideTick ? "hidden" : ""
            )}
          >
            <Check className="h-4 w-4" />
          </span>
        </>
      )}
    </Listbox.Option>
  )
})

SelectItem.displayName = "SelectItem"

// Placeholder components
const SelectLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="py-1.5 pl-8 pr-2 text-sm font-semibold">{children}</div>
)

const SelectSeparator = () => <div className="-mx-1 my-1 h-px bg-muted" />

const SelectScrollUpButton = () => null
const SelectScrollDownButton = () => null

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
